import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Menu, X,
} from 'lucide-react';
import JobastiLogo from '../components/JobastiLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

// ── Brand blue (brighter royal blue, blends into white) ───────────────────────
const BLUE = '#2563EB';
const BLUE_DEEP = '#1D4ED8';
const HERO_BG = `linear-gradient(180deg, #2E6BF2 0%, ${BLUE} 48%, ${BLUE_DEEP} 100%)`;

// ── Recruiting Solutions (Cohere "Command family" card style) ─────────────────
const services = [
  { id: 'ai-matching', title: 'AI Matching', category: 'MATCHING & RANKING',
    description: 'Every CV is read against your role and ranked by fit, with a transparent report explaining exactly why each candidate matches.',
    banner: `radial-gradient(circle at 72% 42%, rgba(37,99,235,0.95) 0%, rgba(0,180,255,0.7) 28%, transparent 60%),
             radial-gradient(circle at 25% 68%, rgba(0,210,255,0.85) 0%, transparent 55%),
             radial-gradient(circle at 50% 25%, rgba(130,170,255,0.6) 0%, transparent 55%),
             #06133a` },
  { id: 'video-interviews', title: 'Video Interviews', category: 'ASYNC INTERVIEWS',
    description: 'Send structured video interviews candidates answer on their own time. Each response returns with an AI summary and comparable scores.',
    banner: `radial-gradient(circle at 70% 40%, rgba(255,77,140,0.95) 0%, rgba(255,120,170,0.7) 28%, transparent 60%),
             radial-gradient(circle at 28% 66%, rgba(190,60,120,0.85) 0%, transparent 55%),
             radial-gradient(circle at 52% 28%, rgba(255,180,210,0.5) 0%, transparent 55%),
             #2a0a1c` },
  { id: 'pipeline', title: 'Applicant Pipeline', category: 'PIPELINE & ATS',
    description: 'Track every candidate from applied to hired on one connected board, and see instantly where a role is slowing down.',
    banner: `radial-gradient(circle at 70% 42%, rgba(16,185,129,0.9) 0%, rgba(45,212,191,0.7) 28%, transparent 60%),
             radial-gradient(circle at 26% 66%, rgba(5,150,120,0.85) 0%, transparent 55%),
             #04211a` },
  { id: 'jobi', title: 'Jobi Assistant', category: 'AI COPILOT',
    description: 'Your recruiting copilot drafts job posts, benchmarks salaries and answers pipeline questions in plain language.',
    banner: `radial-gradient(circle at 70% 40%, rgba(99,102,241,0.95) 0%, rgba(129,140,248,0.7) 28%, transparent 60%),
             radial-gradient(circle at 26% 66%, rgba(76,29,149,0.85) 0%, transparent 55%),
             #0f0a2e` },
  { id: 'agencies', title: 'Agencies & Sourcing', category: 'SOURCING NETWORK',
    description: 'Invite external agents to submit candidates through their own portal, review them side by side and merge the best into your pipeline.',
    banner: `radial-gradient(circle at 70% 40%, rgba(192,38,211,0.9) 0%, rgba(217,70,239,0.7) 28%, transparent 60%),
             radial-gradient(circle at 26% 66%, rgba(126,34,206,0.85) 0%, transparent 55%),
             #1e0a2e` },
  { id: 'analytics', title: 'Analytics & Reports', category: 'HIRING ANALYTICS',
    description: 'Understand time-to-hire, source quality and funnel drop-off with reporting that turns hiring into a measurable process.',
    banner: `radial-gradient(circle at 70% 42%, rgba(245,158,11,0.9) 0%, rgba(251,191,36,0.7) 28%, transparent 60%),
             radial-gradient(circle at 26% 66%, rgba(217,119,6,0.85) 0%, transparent 55%),
             #2a1704` },
];

const partners = ['Northwind', 'Lumenary', 'Tavola', 'Vireo', 'Kastl', 'Orbit'];

const howSteps = [
  { title: 'Create your free account',
    desc: 'Join Jobasti in seconds and tell us what you are looking for — your next great role is closer than you think.',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_70c02982-6ce5-42a9-a35d-655e4757dd5b/artifacts/5slfgh1w_Welcome%20card%402x.webp' },
  { title: 'Build your profile once',
    desc: 'Add your CV from your device, Dropbox or Google Drive — or enter your details by hand. Set it up once and reuse it everywhere.',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_70c02982-6ce5-42a9-a35d-655e4757dd5b/artifacts/i3fgblrp_Profile___deep_blue_2x_white.webp' },
  { title: 'Apply in one click',
    desc: 'Autofill any application with your Jobasti profile and apply to more roles in a fraction of the time.',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_70c02982-6ce5-42a9-a35d-655e4757dd5b/artifacts/jiquwr79_Quick_apply___classic_blue_2x_white.webp' },
  { title: 'Get matched to new roles',
    desc: 'Discover open roles and subscribe to job alerts, so the right opportunities land straight in your inbox.',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_70c02982-6ce5-42a9-a35d-655e4757dd5b/artifacts/oqzeyqtm_Job_alert___soft_blue_2x_white.webp' },
  { title: 'Track every application',
    desc: 'Follow each application from applied to reviewed in one place, so you always know exactly where you stand.',
    image: 'https://customer-assets-4nw71qhi.emergentagent.net/job_70c02982-6ce5-42a9-a35d-655e4757dd5b/artifacts/42vl0w9m_Application_status___cool_grey_2x_white.webp' },
];

// ── Hero video showcase card (from reference "HeroExact") ─────────────────────
const CompanyLanding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const goDemo = () => navigate('/demo');
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  // ── Recruiting Solutions carousel (custom centered scrollbar) ──────────────
  const trackRef = useRef(null);
  const barRef = useRef(null);
  const draggingRef = useRef(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [thumbW, setThumbW] = useState(1);      // 0..1 (visible ratio)

  const onTrackScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
    setThumbW(el.scrollWidth > 0 ? Math.min(1, el.clientWidth / el.scrollWidth) : 1);
  };
  const seekFromClientX = (clientX) => {
    const bar = barRef.current;
    const el = trackRef.current;
    if (!bar || !el) return;
    const rect = bar.getBoundingClientRect();
    const frac = (clientX - rect.left) / rect.width;
    const tw = el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1;
    let p = tw < 1 ? (frac - tw / 2) / (1 - tw) : 0;
    p = Math.max(0, Math.min(1, p));
    el.scrollLeft = p * (el.scrollWidth - el.clientWidth);
  };
  const onBarPointerDown = (e) => { draggingRef.current = true; seekFromClientX(e.clientX); };
  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('[data-carousel-card]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  useEffect(() => {
    onTrackScroll();
    const move = (e) => { if (draggingRef.current) seekFromClientX(e.clientX); };
    const up = () => { draggingRef.current = false; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('resize', onTrackScroll);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('resize', onTrackScroll);
    };
  }, []);

  return (
    <div className="antialiased text-slate-800 bg-white overflow-x-hidden selection:bg-pink-200 selection:text-pink-900" style={{ fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif" }} data-testid="company-landing">
      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="cl-logo">
            <img src="/brand-flame.png" alt="JobastiIQ" className="h-10 w-auto" />
            <span className="text-[11px] font-semibold text-blue-600 bg-blue-50 border border-blue-100 px-2 py-1 rounded-full">
              {t('companyHero.forHire')}
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-9 lg:gap-10 text-[17px] text-slate-700 font-medium">
            <button onClick={() => scrollTo('services')} className="hover:text-slate-900 transition-colors">{t('companyNav.features')}</button>
            <button onClick={() => scrollTo('about')} className="hover:text-slate-900 transition-colors">{t('companyNav.why')}</button>
            <Link to="/demo" className="hover:text-slate-900 transition-colors">{t('companyNav.contact')}</Link>
            <Link to="/for-jobseekers" className="hover:text-slate-900 transition-colors">{t('companyNav.forJobSeekers')}</Link>
          </nav>
          <div className="flex items-center gap-3 lg:gap-4">
            <LanguageSwitcher />
            <Link
              to="/login"
              onClick={() => { try { sessionStorage.setItem('jobasti_landing_source', 'companies'); } catch { /* noop */ } }}
              className="hidden md:inline-flex items-center rounded-full border border-slate-300 px-6 py-2.5 text-[15px] font-semibold text-slate-800 hover:border-slate-400 hover:bg-slate-50 transition-colors"
              data-testid="cl-login-btn"
            >
              {t('common.login')}
            </Link>
            <Link to="/demo" className="hidden md:inline-flex items-center text-white px-6 py-2.5 rounded-full text-[15px] font-semibold transition-colors hover:opacity-90" style={{ backgroundColor: BLUE }} data-testid="cl-nav-cta">
              {t('common.requestDemo')}
            </Link>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-slate-800 hover:bg-slate-100 transition-colors"
              aria-label="Menu"
              aria-expanded={menuOpen}
              data-testid="cl-mobile-menu-btn"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white" data-testid="cl-mobile-menu">
            <div className="px-6 py-4 flex flex-col">
              <button onClick={() => { scrollTo('services'); setMenuOpen(false); }} className="text-left py-3.5 text-lg font-medium text-slate-800 border-b border-slate-100">{t('companyNav.features')}</button>
              <button onClick={() => { scrollTo('about'); setMenuOpen(false); }} className="text-left py-3.5 text-lg font-medium text-slate-800 border-b border-slate-100">{t('companyNav.why')}</button>
              <Link to="/demo" onClick={() => setMenuOpen(false)} className="py-3.5 text-lg font-medium text-slate-800 border-b border-slate-100">{t('companyNav.contact')}</Link>
              <Link to="/for-jobseekers" onClick={() => setMenuOpen(false)} className="py-3.5 text-lg font-medium text-slate-800 border-b border-slate-100">{t('companyNav.forJobSeekers')}</Link>
              <div className="flex flex-col gap-3 pt-5 pb-2">
                <Link
                  to="/login"
                  onClick={() => { try { sessionStorage.setItem('jobasti_landing_source', 'companies'); } catch { /* noop */ } setMenuOpen(false); }}
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-[15px] font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                  data-testid="cl-mobile-login-btn"
                >
                  {t('common.login')}
                </Link>
                <Link
                  to="/demo"
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex items-center justify-center text-white px-6 py-3 rounded-full text-[15px] font-semibold transition-colors hover:opacity-90"
                  style={{ backgroundColor: BLUE }}
                  data-testid="cl-mobile-nav-cta"
                >
                  {t('common.requestDemo')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO (brighter blue, white text) ─────────────────────────────── */}
      <section className="relative text-white min-h-[78vh] flex items-center pt-24 pb-28 overflow-hidden" style={{ background: HERO_BG }} data-testid="hero-section">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="h-[1px] w-10 bg-pink-300/80"></div>
              <span className="text-pink-200 font-semibold tracking-[0.3em] text-xs uppercase notranslate" translate="no">JobastiIQ</span>
              <div className="h-[1px] w-10 bg-pink-300/80"></div>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[80px] font-serif font-medium leading-[1.05] mb-8" data-testid="hero-headline">
              Recruiting that <span className="font-bold italic">works with you</span>
            </h1>
            <p className="text-lg lg:text-xl text-blue-50/90 mb-10 leading-relaxed max-w-2xl mx-auto" data-testid="hero-subhead">
              AI matching, structured video interviews and your whole pipeline in one place — built for the teams
              that hire the people who move the world forward.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={goDemo} className="bg-white px-8 py-3.5 rounded-full font-bold hover:bg-pink-500 hover:text-white transition-all duration-300 flex items-center gap-2 group" style={{ color: BLUE_DEEP }} data-testid="hero-cta-demo-btn">
                {t('common.requestDemo')}
                <ArrowRight size={18} className="text-pink-500 group-hover:text-white transition-colors" />
              </button>
              <button onClick={() => scrollTo('services')} className="px-8 py-3.5 rounded-full font-bold border border-white/50 hover:border-pink-300 hover:text-pink-200 transition-all duration-300 flex items-center gap-2" data-testid="hero-cta-explore-btn">
                Explore features
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES (Cohere-style cards, white bg) ──────────────────────── */}
      <section id="services" className="bg-white py-24 lg:py-28 relative" data-testid="cl-services">
        <div className="container mx-auto px-6 flex">
          <div className="hidden lg:flex flex-col items-center mr-16 pt-10">
            <div className="h-24 w-[1px] bg-blue-400 mb-4"></div>
            <span className="vertical-text text-blue-500 font-bold tracking-[0.2em] text-sm uppercase">What we offer</span>
            <div className="h-full w-[1px] bg-gray-100 mt-4"></div>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4 lg:hidden">
              <div className="h-[1px] w-12 bg-blue-400"></div>
              <span className="text-blue-500 font-bold tracking-[0.2em] text-xs uppercase">What we offer</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-serif font-medium text-navy-900 mb-8">
              Recruiting Solutions
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mb-14 leading-relaxed">
              A connected platform that takes you from an open role to a signed offer — with AI where it helps
              and human judgement where it counts.
            </p>

            {/* Carousel with side overlay arrows */}
            <div className="relative">
              <div
                ref={trackRef}
                onScroll={onTrackScroll}
                className="flex gap-6 overflow-x-auto no-scrollbar pb-2"
                data-testid="cl-services-carousel"
              >
                {services.map((service) => (
                  <div
                    key={service.id}
                    data-carousel-card
                    onClick={goDemo}
                    className="shrink-0 w-[85%] sm:w-[48%] lg:w-[calc((100%-3rem)/3)] rounded-3xl p-4 sm:p-5 border border-white/[0.08] transition-all duration-300 flex flex-col justify-between group cursor-pointer shadow-lg hover:shadow-2xl hover:-translate-y-1 hover:brightness-110"
                    style={{ backgroundColor: BLUE }}
                    data-testid={`cl-service-${service.id}`}
                  >
                    <div>
                      {/* Gradient banner + feature name (no logo) */}
                      <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden relative flex items-center justify-center select-none shadow-inner border border-white/10">
                        <div className="absolute inset-0 group-hover:scale-105 transition-transform duration-700 ease-out" style={{ background: service.banner, filter: 'blur(16px) contrast(1.15)' }} />
                        <div className="absolute inset-0 bg-black/20" />
                        <span className="relative z-10 font-serif text-2xl sm:text-[28px] font-medium tracking-tight text-white drop-shadow-lg text-center px-4">
                          {service.title}
                        </span>
                      </div>

                      <div className="mt-4 text-[11px] font-mono tracking-wider text-blue-100/90 uppercase">
                        {service.category}
                      </div>
                      <h3 className="mt-2.5 text-base sm:text-[17px] font-medium text-white leading-snug tracking-tight group-hover:text-pink-100 transition-colors">
                        {service.description}
                      </h3>
                    </div>

                    {/* Notch arrow footer */}
                    <div className="mt-6 pt-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-50 group-hover:text-white transition-colors">Learn more</span>
                      <div className="w-10 h-10 rounded-xl bg-white/15 border border-white/15 flex items-center justify-center text-white group-hover:bg-pink-500 group-hover:border-pink-500 group-hover:shadow-[0_0_15px_rgba(255,77,140,0.5)] transition-all duration-300">
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </div>
                ))}
                {/* Trailing spacer so last card can scroll fully into view */}
                <div aria-hidden="true" className="shrink-0 w-[15%] sm:w-[10%] lg:w-[8%]" data-testid="cl-services-spacer" />
              </div>

              {/* Overlay arrows on the sides */}
              <button
                onClick={() => nudge(-1)}
                className="absolute -left-3 top-1/2 -translate-y-1/2 z-40 w-11 h-11 rounded-full bg-white shadow-xl border border-slate-200 flex items-center justify-center text-slate-800 hover:bg-slate-50 hover:scale-105 transition-all"
                aria-label="Previous"
                data-testid="cl-services-prev"
              >
                <ArrowRight size={20} className="rotate-180" />
              </button>
              <button
                onClick={() => nudge(1)}
                className="absolute right-28 top-1/2 -translate-y-1/2 z-40 w-11 h-11 rounded-full bg-white shadow-xl border border-slate-200 flex items-center justify-center text-slate-800 hover:bg-slate-50 hover:scale-105 transition-all"
                aria-label="Next"
                data-testid="cl-services-next"
              >
                <ArrowRight size={20} />
              </button>
            </div>

            {/* Centered scrollbar */}
            <div className="mt-8 flex justify-center">
              <div
                ref={barRef}
                onPointerDown={onBarPointerDown}
                className="relative h-2 w-48 sm:w-64 rounded-full bg-slate-200 cursor-pointer touch-none"
                data-testid="cl-services-scrollbar"
              >
                <div
                  className="absolute top-0 h-full rounded-full transition-[left,width] duration-150 ease-out"
                  style={{ backgroundColor: BLUE, width: `${Math.max(thumbW * 100, 12)}%`, left: `${progress * (100 - Math.max(thumbW * 100, 12))}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNERS ─────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-24 relative overflow-hidden" data-testid="cl-partners">
        <div className="container mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6 order-1 lg:order-3">
            <div className="flex items-center gap-4 mb-4 lg:hidden">
              <div className="h-[1px] w-12 bg-blue-400"></div>
              <span className="text-blue-500 font-bold tracking-[0.2em] text-xs uppercase">Trusted by teams</span>
            </div>

            <h2 className="text-4xl font-serif font-medium text-navy-900 mb-6">
              Trusted by hiring teams
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Fast-growing companies across tech, healthcare and industry use JobastiIQ to hire with more signal
              and less noise — replacing gut-feel guesswork with clear, explainable decisions.
            </p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              From your first hire to your hundredth, the platform flexes to how your team works and connects to
              the tools you already rely on.
            </p>
            <p className="text-gray-600 leading-relaxed">
              We believe great hiring is fair, fast and human — and that the right technology should make it easier,
              not more impersonal.
            </p>
          </div>

          <div className="hidden lg:flex lg:col-span-1 lg:order-2 justify-center relative">
            <div className="h-full w-[1px] bg-blue-300 absolute left-1/2"></div>
            <span className="bg-gray-50 py-4 vertical-text text-blue-500 font-bold tracking-[0.2em] text-xs uppercase z-10">Built for hiring</span>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-8 items-center justify-items-center opacity-70 order-2 lg:order-1">
            {partners.map((partner, i) => (
              <div key={i} className="font-bold text-2xl text-gray-400 hover:text-navy-900 transition-colors cursor-default notranslate" translate="no">
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT ────────────────────────────────────────────────────────── */}
      <section id="about" className="bg-white py-24 relative" data-testid="cl-about">
        <div className="container mx-auto px-6 flex">
          <div className="hidden lg:flex flex-col items-center mr-16">
            <div className="h-24 w-[1px] bg-blue-400 mb-4"></div>
            <span className="vertical-text text-blue-500 font-bold tracking-[0.2em] text-sm uppercase">Who we are</span>
            <div className="h-full w-[1px] bg-gray-100 mt-4"></div>
          </div>

          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-4 lg:hidden">
              <div className="h-[1px] w-12 bg-blue-400"></div>
              <span className="text-blue-500 font-bold tracking-[0.2em] text-xs uppercase">Who we are</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-serif font-medium text-navy-900 mb-8">
              About <span className="notranslate" translate="no">JobastiIQ</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              JobastiIQ is a modern applicant tracking system built for teams that care about hiring well. We bring
              AI matching, structured video interviews, sourcing and analytics into one place, so recruiters spend
              less time on admin and more time with people.
            </p>
            <p className="text-gray-600 text-lg mb-10 leading-relaxed">
              Whether you are filling your first role or scaling a whole department, our team is here to help you
              move faster with confidence — from your first job post to a signed offer and beyond.
            </p>

            <button
              onClick={goDemo}
              className="text-white px-8 py-3.5 rounded-full font-bold hover:bg-pink-500 transition-all duration-300 flex items-center gap-2 group shadow-lg"
              style={{ backgroundColor: BLUE_DEEP }}
              data-testid="cl-about-cta"
            >
              Talk to our team
              <ArrowRight size={18} className="text-pink-300 group-hover:text-white transition-colors" />
            </button>
          </div>
        </div>
      </section>

      {/* ── WHAT'S POSSIBLE (replaces Success Stories) ───────────────────── */}
      <section className="bg-white text-[#181816] py-16 sm:py-24 relative overflow-hidden" data-testid="cl-whats-possible">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Two showcase cards */}
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[1px] w-12 bg-blue-400"></div>
              <span className="text-blue-500 font-bold tracking-[0.2em] text-xs uppercase">What&rsquo;s possible</span>
              <div className="h-[1px] w-12 bg-blue-400"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch">

              {/* Card 1 — Belief (photo fills card, text overlaid) */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-xl border border-white/5 min-h-[460px] sm:min-h-[520px] text-left group transition-all duration-300 hover:shadow-[0_20px_45px_rgba(14,7,37,0.45)] hover:-translate-y-1 bg-[#0e0725]" data-testid="cl-wp-card-belief">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_70c02982-6ce5-42a9-a35d-655e4757dd5b/artifacts/tpgv0axx_download.png"
                  alt="Hiring team collaborating"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/10"></div>
                <div className="relative z-10 p-7 sm:p-9 max-w-sm">
                  <div className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] text-white/75 uppercase mb-3 font-mono">Our belief</div>
                  <h3 className="text-2xl sm:text-3xl lg:text-[34px] tracking-tight leading-[1.18] font-serif text-white drop-shadow-md">
                    <span className="font-extrabold text-white">Fair</span>{' '}
                    <span className="font-normal text-white/85">hiring, powered by explainable AI.</span>
                  </h3>
                </div>
              </div>

              {/* Card 2 — Build (photo fills card, text overlaid) */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-xl border border-[#b2eccf] min-h-[460px] sm:min-h-[520px] text-left group transition-all duration-300 hover:shadow-[0_20px_45px_rgba(178,236,207,0.5)] hover:-translate-y-1 bg-[#d4faea]" data-testid="cl-wp-card-build">
                <img
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_70c02982-6ce5-42a9-a35d-655e4757dd5b/artifacts/7gdlod47_66ce09f23bfc5382e0a79e3a_content-gallery-1.webp"
                  alt="Recruiter and candidate in conversation"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/30 to-black/5"></div>
                <div className="relative z-10 p-7 sm:p-9 max-w-sm">
                  <div className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] text-white/80 uppercase mb-3 font-mono">Get started</div>
                  <h3 className="text-2xl sm:text-3xl lg:text-[34px] tracking-tight leading-[1.18] font-serif text-white drop-shadow-md">
                    <span className="font-extrabold text-[#93b4ff]">Build</span>{' '}
                    <span className="font-medium text-white">the team that builds your future.</span>
                  </h3>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section id="blog" className="w-full relative" data-testid="cl-howitworks">
        {/* Desktop: interactive two columns */}
        <div className="hidden lg:grid lg:grid-cols-2" style={{ background: `linear-gradient(180deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)` }}>
          <div className="relative min-h-[560px] overflow-hidden">
            {howSteps.map((s, i) => (
              <div key={i} className={`absolute inset-0 flex items-center justify-center p-12 transition-opacity duration-500 ${activeStep === i ? 'opacity-100' : 'opacity-0'}`}>
                <img src={s.image} alt={s.title} className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" />
              </div>
            ))}
          </div>

          <div className="text-white px-12 xl:px-16 py-16 flex flex-col justify-center">
            <div className="w-full max-w-lg mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-[1px] w-10 bg-blue-300/70"></div>
                <span className="text-blue-100 font-semibold tracking-[0.28em] text-xs uppercase">How it works</span>
              </div>
              <h2 className="font-serif text-3xl xl:text-4xl font-medium leading-[1.1] mb-8">
                From sign-up to your <span className="italic">next role</span>
              </h2>
              <div className="flex flex-col">
                {howSteps.map((s, i) => (
                  <button
                    key={i}
                    onMouseEnter={() => setActiveStep(i)}
                    onClick={() => setActiveStep(i)}
                    className="text-left border-b border-white/15 py-4 group focus:outline-none"
                    data-testid={`cl-how-step-${i}`}
                  >
                    <div className="flex items-center gap-3">
                      <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ${activeStep === i ? 'text-blue-200' : 'text-blue-200/60 group-hover:text-blue-200'}`} />
                      <span className={`font-serif text-lg xl:text-xl transition-colors duration-300 ${activeStep === i ? 'text-white' : 'text-blue-100/70 group-hover:text-white'}`}>
                        {s.title}
                      </span>
                    </div>
                    <div className={`grid transition-all duration-300 ease-out ${activeStep === i ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`}>
                      <p className="pl-7 overflow-hidden text-sm text-blue-100/85 leading-relaxed">{s.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: swipeable card carousel */}
        <div className="lg:hidden text-white px-6 py-14" style={{ background: `linear-gradient(180deg, ${BLUE} 0%, ${BLUE_DEEP} 100%)` }}>
          <div className="flex items-center gap-4 mb-5">
            <div className="h-[1px] w-10 bg-blue-300/70"></div>
            <span className="text-blue-100 font-semibold tracking-[0.28em] text-xs uppercase">How it works</span>
          </div>
          <h2 className="font-serif text-3xl font-medium leading-[1.1] mb-2">
            From sign-up to your <span className="italic">next role</span>
          </h2>
          <p className="text-blue-100/70 text-xs mb-6">Swipe to explore &rarr;</p>
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-6 px-6 pb-2">
            {howSteps.map((s, i) => (
              <div key={i} className="snap-center shrink-0 w-[80%] sm:w-[62%]" data-testid={`cl-how-slide-${i}`}>
                <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 aspect-[3/4] flex items-center justify-center p-3">
                  <img src={s.image} alt={s.title} className="max-w-full max-h-full object-contain rounded-lg" />
                </div>
                <h3 className="mt-4 font-serif text-xl">{i + 1}. {s.title}</h3>
                <p className="mt-1 text-sm text-blue-100/80 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA + FOOTER ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ backgroundColor: BLUE_DEEP }} data-testid="cl-cta-footer">
        <div className="absolute top-0 right-0 h-full w-1/2 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-blue-400/10 blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-[360px] h-[360px] rounded-full bg-pink-400/10 blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 py-24 lg:py-32 relative z-10 flex flex-col items-center text-center">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium text-white max-w-2xl leading-[1.08]">
            Ready to make great hiring feel <span className="italic">effortless</span>?
          </h2>
          <button
            onClick={goDemo}
            className="mt-10 inline-flex items-center gap-2 bg-white px-8 py-4 rounded-full font-bold hover:bg-blue-50 transition-colors group"
            style={{ color: BLUE_DEEP }}
            data-testid="cl-cta-btn"
          >
            {t('common.requestDemo')}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" style={{ color: BLUE }} />
          </button>
        </div>

        <div className="border-t border-white/10 relative z-10">
          <div className="container mx-auto px-6 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-blue-100/60">
            <div className="flex items-center gap-3">
              <JobastiLogo mono height={26} />
              <span>&copy; {new Date().getFullYear()} All rights reserved.</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/for-jobseekers" className="hover:text-white transition-colors">{t('companyFooter.forJobSeekers')}</Link>
              <Link to="/login" className="hover:text-white transition-colors">{t('companyFooter.login')}</Link>
              <a href="mailto:hello@jobasti.com" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyLanding;
